import { useEffect, useRef, useState } from "react";
import { ApiError, ApiSessionChangedError, type ApiClient } from "../../../api/api-client";
import { netBuySchema, type NetBuyResponse } from "../../../api/schemas";
import type { NetBuyUpdatesView } from "../types";
import { errorMessage, sameAddress } from "../../../state/model-helpers";
import { ELYSIUM, sameIdentity } from "../../../config/elysium";

type NetBuyRead = {
  contextKey: string;
  data: NetBuyResponse | null;
  lastDisplayData: NetBuyResponse | null;
  status: { loading: boolean; error: string | null };
  updates: NetBuyUpdatesView;
};
const emptyRead = (contextKey: string, loading: boolean): NetBuyRead => ({ contextKey, data: null, lastDisplayData: null,
  status: { loading, error: null }, updates: { mode: "IDLE", error: null } });

export function useNetBuyLive({ api, enabled, chainId, tokenAddress, account, contextKey, readContextKey,
  refreshRevision }: {
  api: ApiClient; enabled: boolean; chainId: number | undefined; tokenAddress: string | undefined;
  account: string | null; contextKey: string; readContextKey(): string; refreshRevision: number;
}): NetBuyRead {
  const [stored, setStored] = useState<NetBuyRead>(() => emptyRead("", false));
  const generation = useRef(0);
  const refreshReads = useRef<() => void>(() => {});
  const committedRefresh = useRef(refreshRevision);
  const lastRefresh = useRef(refreshRevision);
  useEffect(() => { committedRefresh.current = refreshRevision; }, [refreshRevision]);

  useEffect(() => {
    const revision = ++generation.current;
    const available = enabled && Boolean(chainId && tokenAddress && account);
    setStored(previous => {
      const last = previous.lastDisplayData;
      return { ...emptyRead(contextKey, available),
        lastDisplayData: previous.contextKey === contextKey && last?.chainId === chainId &&
          sameAddress(last?.tokenAddress, tokenAddress) && sameAddress(last?.wallet, account) ? last : null };
    });
    lastRefresh.current = committedRefresh.current;
    if (!available || !tokenAddress || !account || !chainId) return;
    const abort = new AbortController();
    const current = () => !abort.signal.aborted && revision === generation.current && contextKey === readContextKey();
    const path = `/tokens/${tokenAddress}/wallets/${account}/net-buy`;
    let pending = false;
    let queued = false;
    let lastStarted = -Infinity;
    let pushRevision = 0;
    let live = false;
    let streamAbort: AbortController | null = null;
    let failures = 0;
    let retryMs = 3_000;
    let retryAt = 0;
    let retryAfterAt = 0;
    let stopRetry = false;
    const publish = (change: (previous: NetBuyRead) => NetBuyRead) => {
      if (current()) setStored((previous) => previous.contextKey === contextKey && current() ? change(previous) : previous);
    };
    const setUpdates = (mode: NetBuyUpdatesView["mode"], error: string | null = null) =>
      publish((previous) => ({ ...previous, updates: { mode, error } }));
    const validateIdentity = (value: NetBuyResponse) => {
      if (value.chainId !== chainId || !sameAddress(value.tokenAddress, tokenAddress) || !sameAddress(value.wallet, account) ||
        chainId === ELYSIUM.chainId && (value.source !== "MERKLE_V4_POOL_EVENTS" || value.poolAddress !== null ||
          !sameIdentity(value.poolManager, ELYSIUM.poolManager) || !sameIdentity(value.poolId, ELYSIUM.poolId) ||
          !sameIdentity(value.tokenAddress, ELYSIUM.token))) {
        throw new ApiError(502, "NET_BUY_CONTEXT_MISMATCH", "Net-buy snapshot belongs to another context", null, null);
      }
      return value;
    };
    const load = (force = false) => {
      if (!current()) return;
      if (pending) { if (force) queued = true; return; }
      if (!force && (live || performance.now() - lastStarted < 15_000)) return;
      pending = true;
      lastStarted = performance.now();
      const atDispatch = pushRevision;
      const streamOwnedAtDispatch = live;
      publish((previous) => ({ ...previous, status: { loading: true, error: null } }));
      (async () => {
        try {
          const data = validateIdentity(await api.get(path, netBuySchema, abort.signal));
          // A stream snapshot wins even when this GET was dispatched first.
          if (!streamOwnedAtDispatch && !live && atDispatch === pushRevision) publish((previous) => ({ ...previous, data, lastDisplayData: data,
            status: { loading: false, error: null } }));
        } catch (error) {
          if (!streamOwnedAtDispatch && !live && atDispatch === pushRevision) publish((previous) => ({ ...previous, data: null,
            status: { loading: false, error: errorMessage(error) } }));
        } finally {
          pending = false;
          publish((previous) => ({ ...previous, status: { ...previous.status, loading: false } }));
          if (queued && current()) { queued = false; load(true); }
        }
      })().catch(() => console.error("Net-buy read state could not be published."));
    };
    const connect = () => {
      if (!current() || streamAbort || stopRetry || document.visibilityState === "hidden" ||
        performance.now() < Math.max(retryAt, retryAfterAt)) return;
      const connection = new AbortController();
      streamAbort = connection;
      setUpdates("CONNECTING");
      api.stream(`${path}/stream`, {
        signal: connection.signal,
        onRetry: (value) => { retryMs = Math.max(value, 1_000); },
        onEvent: (event) => {
          if (!current() || connection.signal.aborted) return;
          if (event.event === "error") throw new ApiError(503, "NET_BUY_STREAM_ERROR", "Net-buy stream reported an error", null, null);
          if (event.event !== "net-buy") return;
          let payload: unknown;
          try { payload = JSON.parse(event.data) as unknown; }
          catch { throw new ApiError(502, "INVALID_SERVER_RESPONSE", "Invalid net-buy stream JSON", null, null); }
          const parsed = netBuySchema.safeParse(payload);
          if (!parsed.success) throw new ApiError(502, "INVALID_SERVER_RESPONSE", "Invalid net-buy stream snapshot", null, null);
          const data = validateIdentity(parsed.data);
          pushRevision++;
          live = true;
          failures = 0;
          publish((previous) => ({ ...previous, data, lastDisplayData: data, status: { loading: false, error: null },
            updates: { mode: "LIVE", error: null } }));
        },
      }).then(() => {
        if (!connection.signal.aborted) throw new ApiError(503, "STREAM_CLOSED", "Net-buy stream closed", null, null);
      }).catch((error: unknown) => {
        if (!current() || connection.signal.aborted) return;
        live = false;
        if (chainId === ELYSIUM.chainId) publish(previous => ({ ...previous, data: null,
          status: { loading: false, error: "NET_BUY_STREAM_STALE" } }));
        // New connections receive a full current snapshot; do not replay old event IDs.
        failures = Math.min(failures + 1, 6);
        const delay = Math.max(retryMs, Math.min(1_000 * 2 ** (failures - 1), 30_000)) * (1 + Math.random() * 0.2);
        retryAfterAt = performance.now() + (error instanceof ApiError ? error.retryAfterMs ?? 0 : 0);
        retryAt = Math.max(performance.now() + delay, retryAfterAt);
        stopRetry = error instanceof ApiSessionChangedError || error instanceof ApiError && [401, 403, 404, 405, 410].includes(error.status);
        setUpdates("POLLING", errorMessage(error));
        load();
      }).finally(() => { if (streamAbort === connection) streamAbort = null; });
    };
    const refresh = () => {
      // Without a shared GET/stream revision, reconnect to obtain a fresh authoritative stream snapshot.
      live = false;
      if (chainId === ELYSIUM.chainId) publish(previous => ({ ...previous, data: null }));
      streamAbort?.abort(); streamAbort = null;
      stopRetry = false; retryAt = 0;
      load(true); connect();
    };
    refreshReads.current = refresh;
    load(true);
    connect();
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      load(); connect();
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") {
        live = false;
        if (chainId === ELYSIUM.chainId) publish(previous => ({ ...previous, data: null }));
        streamAbort?.abort(); streamAbort = null;
        publish((previous) => ({ ...previous, updates: { ...previous.updates, mode: "PAUSED" } }));
      } else {
        // connect() can stay blocked by stopRetry or Retry-After while GET polling resumes.
        publish((previous) => previous.updates.mode !== "PAUSED" ? previous :
          { ...previous, updates: { ...previous.updates, mode: "POLLING" } });
        load(true); connect();
      }
    };
    if (document.visibilityState === "hidden") setUpdates("PAUSED");
    const timer = setInterval(tick, 1_000);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      abort.abort(); streamAbort?.abort(); clearInterval(timer);
      document.removeEventListener("visibilitychange", visibility);
      if (refreshReads.current === refresh) refreshReads.current = () => {};
    };
  }, [api, enabled, chainId, tokenAddress, account, contextKey, readContextKey]);
  useEffect(() => {
    if (lastRefresh.current !== refreshRevision) {
      lastRefresh.current = refreshRevision;
      refreshReads.current();
    }
  }, [refreshRevision]);
  return stored.contextKey === contextKey ? stored : emptyRead(contextKey, enabled && Boolean(tokenAddress && account));
}
